import './index.scss';

import React, { useState, useEffect } from 'react';
import { useIntl, getLocale } from 'umi';
import { Segment, Icon } from 'tea-component';
import ArrowBottom from '@/pages/intelligence/assets/svg/selectIcon/arrow-bottom.svg';
import { mapColor, lineColor } from '@/pages/intelligence/pages/MarketProfile/_config/Const';
import { mapZoom, sortArrDesc } from '@/pages/intelligence/pages/MarketProfile/_util';
import { formatNum } from '@/pages/intelligence/utils/commonUtils';
import * as echarts from 'echarts/core';
import type { EChartsOption, SeriesOption, MapSeriesOption, GeoJSON } from 'echarts';
import ChartBubble from '@/pages/intelligence/components/ChartBubble';
import { importJSON } from '@/pages/intelligence/utils/echartsUtils';
import { getPieces } from '../../_util';

import { ChartCardProps, MapDataItem, TooltipParams, RegionCountry } from './types';

const mapCache = {
  worldMap: null as GeoJSON | null,
};

export default function ChartCard(props: ChartCardProps) {
  const { formatMessage } = useIntl();
  const locale = getLocale();
  const {
    options,
    sortbyText,
    activeTab,
    pageId,
    mainSegment = 'Revenue',
    onSegmentChange,
    digitalVal,
    physicalVal,
    totalRevenue,
    totalPlayer,
    selectedKeys,
    lineTrendData,
    chartType,
    selectedKeysColor,
    regionsCountries,
  } = props;

  const [map, setMap] = useState<GeoJSON>();
  const [mapLoading, setMapLoading] = useState<boolean>(true);

  const isMounted = React.useRef(true);

  const isEN = getLocale() === 'en-US';
  const allMarkets = regionsCountries?.map((area: RegionCountry) => area.country_set).flat() || [];

  const toTranslateCountry = (code: string) => {
    return allMarkets.find(
      (country) => country.country_name_en?.toLocaleLowerCase() === code?.toLocaleLowerCase(),
    )?.[isEN ? 'country_name_en' : 'country_name_ch'];
  };

  // const getMapChart = async () => {
  //   try {
  //     if (mapCache.worldMap) {
  //       if (chartType === 'chart-map' && !map && isMounted.current) {
  //         console.debug('Using cached map data');
  //         setMap(mapCache.worldMap);
  //         setMapLoading(false);
  //       }
  //       return;
  //     }

  //     if (echarts.getMap('world')) {
  //       const existingMap = echarts.getMap('world').geoJson;

  //       if (chartType === 'chart-map' && !map && isMounted.current) {
  //         console.debug('MAP exist and echarts getMap', {
  //           existingMap,
  //           map,
  //           isMounted: isMounted.current,
  //         });

  //         setMap(existingMap);
  //         mapCache.worldMap = existingMap;
  //       }

  //       console.debug('MAP has mounted but not map', {
  //         chartType,
  //         map,
  //         isMounted: isMounted.current,
  //       });
  //     } else {
  //       const mapData = (await importJSON(
  //         'https://ogdb-cdn.intlgame.cn/front/static/world.json',
  //       )) as GeoJSON;

  //       if (chartType === 'chart-map' && isMounted.current) {
  //         setMap(mapData);
  //         mapCache.worldMap = mapData;
  //         echarts.registerMap('world', mapData);

  //         console.debug('Fetch GeoJSON and Map registered', { mapData });
  //       }

  //       console.debug('Fetched and chartType or isMounted is no fulfilled', {
  //         mapData,
  //         chartType,
  //         isMounted: isMounted.current,
  //       });
  //     }
  //   } catch (e) {
  //     console.error(e);
  //   }
  // };

  const getMapChart = async () => {
    try {
      // 检查是否已经有缓存
      if (mapCache.worldMap) {
        if (!map && isMounted.current) {
          // console.debug('Using cached map data');
          setMap(mapCache.worldMap);
          setMapLoading(false);
        }
        return;
      }

      // 检查echarts是否已注册世界地图
      if (echarts.getMap('world')) {
        const existingMap = echarts.getMap('world').geoJson;

        if (existingMap && isMounted.current) {
          // console.debug('Using existing echarts map');
          mapCache.worldMap = existingMap;
          setMap(existingMap);
          setMapLoading(false);
        }
      } else {
        // console.debug('Fetching new map data...');

        const mapData = (await importJSON(
          'https://ogdb-cdn.intlgame.cn/front/static/world.json',
        )) as GeoJSON;

        if (mapData && isMounted.current) {
          mapCache.worldMap = mapData;
          echarts.registerMap('world', mapData);
          setMap(mapData);
          setMapLoading(false);

          // console.debug('Map data loaded and registered', { mapData });
        }
      }
    } catch (e) {
      console.error('Failed to load map data:', e);
      setMapLoading(false);
    }
  };

  // console.debug('map rending', { map, props, isMounted: isMounted.current });

  useEffect(() => {
    getMapChart();
    window.addEventListener('reset', resizeHandler);

    return () => {
      isMounted.current = false;
      window.removeEventListener('reset', resizeHandler);
    };
  }, []);

  const worldMapInstance = (
    data: MapDataItem[],
    containId: string,
    mapColor: string[],
    seriesName?: string,
  ) => {
    const chartDom = document.getElementById(containId);
    if (chartDom) {
      let myChart = echarts.getInstanceByDom(chartDom);
      if (myChart == null) {
        myChart = echarts.init(chartDom);
      }

      const dealData =
        data?.map((item) => ({
          ...item,
          name: toTranslateCountry(item.name),
        })) || [];

      const backNameMap: Record<string, string> = {};
      allMarkets?.forEach((country) => {
        backNameMap[country.country_name_en] = country.country_name_ch;
      });

      const tapeoption = {
        tooltip: {
          trigger: 'item',
          padding: 0,
          borderWidth: 0,
          formatter: (params: TooltipParams) => {
            return mapTooltip(params);
          },
          label: { show: false },
        },
        visualMap: [
          {
            realtime: false,
            calculable: false,
            type: 'piecewise',
            min: 0,
            max: 10000000000,
            itemWidth: 14,
            itemHeight: 40,
            inverse: false,
            itemGap: 0,
            itemSymbol: 'rect',
            top: '8%',
            left: '1%',
            // textGap:10,
            textStyle: {
              lineHeight: 40,
              color: '#838697',
              fontSize: 14,
            },
            // 点击后的颜色
            outOfRange: {
              color: '#ffdf33',
              symbolSize: [30, 100],
            },
            pieces: getPieces(data),
          },
        ],
        series: [
          {
            type: 'map',
            mapType: 'world',
            roam: 'move',
            itemStyle: {
              normal: {
                borderColor: 'rgba(190, 190, 190,0.5)',
                borderWidth: 1,
                areaColor: '#f2f2f2',
              },
              emphasis: {
                // borderColor: '#000',
                // borderWidth: 1.5,
                label: { show: false },
                itemStyle: {
                  borderColor: '#000',
                  borderWidth: '2',
                  borderType: 'solid',
                  shadowColor: 'rgba(0, 0, 0, 0.5)',
                  shadowBlur: 10,
                },
              },
            },
            data: dealData,
            nameMap: getLocale() === 'zh-CN' ? backNameMap : null,
          },
        ],
      };
      tapeoption && myChart.setOption(tapeoption, true);
      myChart.resize();
    }
  };

  // const worldMapInstance = (
  //   data: MapDataItem[],
  //   containId: string,
  //   mapColor: string[],
  //   seriesName?: string,
  // ) => {
  //   const chartDom = document.getElementById(containId);
  //   if (!chartDom) {
  //     console.warn(`Chart container ${containId} not found`);
  //     return;
  //   }

  //   if (!echarts.getMap('world')) {
  //     console.warn('World map not registered in echarts');
  //     if (mapCache.worldMap) {
  //       echarts.registerMap('world', mapCache.worldMap);
  //     } else {
  //       return;
  //     }
  //   }

  //   try {
  //     let myChart = echarts.getInstanceByDom(chartDom);
  //     if (myChart == null) {
  //       myChart = echarts.init(chartDom);
  //     }

  //     const dealData =
  //       data?.map((item) => ({
  //         ...item,
  //         name: toTranslateCountry(item.name),
  //       })) || [];

  //     const backNameMap: Record<string, string> = {};
  //     allMarkets?.forEach((country) => {
  //       backNameMap[country.country_name_en] = country.country_name_ch;
  //     });

  //     const tapeoption = {
  //       tooltip: {
  //         trigger: 'item',
  //         padding: 0,
  //         borderWidth: 0,
  //         formatter: (params: TooltipParams) => {
  //           return mapTooltip(params);
  //         },
  //         label: { show: false },
  //       },
  //       visualMap: [
  //         {
  //           realtime: false,
  //           calculable: false,
  //           type: 'piecewise',
  //           min: 0,
  //           max: 10000000000,
  //           itemWidth: 14,
  //           itemHeight: 40,
  //           inverse: false,
  //           itemGap: 0,
  //           itemSymbol: 'rect',
  //           top: '8%',
  //           left: '1%',
  //           // textGap:10,
  //           textStyle: {
  //             lineHeight: 40,
  //             color: '#838697',
  //             fontSize: 14,
  //           },
  //           // 点击后的颜色
  //           outOfRange: {
  //             color: '#ffdf33',
  //             symbolSize: [30, 100],
  //           },
  //           pieces: getPieces(data),
  //         },
  //       ],
  //       series: [
  //         {
  //           type: 'map',
  //           mapType: 'world',
  //           roam: 'move',
  //           itemStyle: {
  //             normal: {
  //               borderColor: 'rgba(190, 190, 190,0.5)',
  //               borderWidth: 1,
  //               areaColor: '#f2f2f2',
  //             },
  //             emphasis: {
  //               // borderColor: '#000',
  //               // borderWidth: 1.5,
  //               label: { show: false },
  //               itemStyle: {
  //                 borderColor: '#000',
  //                 borderWidth: '2',
  //                 borderType: 'solid',
  //                 shadowColor: 'rgba(0, 0, 0, 0.5)',
  //                 shadowBlur: 10,
  //               },
  //             },
  //           },
  //           data: dealData,
  //           nameMap: getLocale() === 'zh-CN' ? backNameMap : null,
  //         },
  //       ],
  //     };

  //     console.debug('Setting chart options', { containId, dataLength: dealData.length });
  //     tapeoption && myChart.setOption(tapeoption, true);
  //     myChart.resize();
  //   } catch (e) {
  //     console.error('Failed to initialize map chart:', e);
  //   }
  // };

  const mapTooltip = (params: TooltipParams) => {
    const mapSerieName = localStorage.getItem('marketProfileChartCard');
    const name =
      mapSerieName == 'Downloads'
        ? `${formatMessage({ id: 'dbi_downloads' })}:`
        : mapSerieName == 'TotalRevenue'
          ? `${formatMessage({ id: 'dbi_total_rev' })}:`
          : mapSerieName == 'TotalPlayer'
            ? `${formatMessage({ id: 'dbi_total_player' })}:`
            : `${formatMessage({ id: 'dbi_revenue_$' })}:`;

    // 对于没有数据的国家，params.data 是 undefined，但 params.name 存在
    // 我们需要翻译国家名称并将其添加到 params 中
    let translatedName = toTranslateCountry(params.name) || params.name;

    // 特殊情况：dataIndex 为 219 的塔吉克斯坦地区在 GeoJSON 中没有名称
    // 我们在此明确映射它以确保其正确显示
    if ((!translatedName || translatedName === '') && params.dataIndex === 219) {
      const locale = getLocale();
      translatedName = locale === 'zh-CN' ? '塔吉克斯坦' : 'Tajikistan';
    }

    return ChartBubble({
      data: {
        ...params,
        name: translatedName,
      },
      titleNow: '',
      topChartsType: 'marketProfile-map',
      imageType: 'mobile',
      leftContent: formatMessage({ id: 'dbi_market_total_game' }),
      //   chartType,
      headerLeftTitle: name,
      dateType: 'month',
      ifNoBarImg: true,
    });
  };

  const refreshMapData = (containId: string, data: any, isRevenue?: boolean) => {
    const dealData =
      data?.map((item: any) => ({
        ...item,
        name: toTranslateCountry(item.name),
      })) || [];
    const chartDom = document.getElementById(containId);
    if (chartDom) {
      const myChart = echarts.getInstanceByDom(chartDom);
      const option = myChart?.getOption();

      if (option && myChart) {
        // @ts-ignore
        option.visualMap[0].pieces = getPieces(data);
        // @ts-ignore
        option.series[0].data = dealData;
        myChart.setOption(option);
        myChart.resize();
      }
    }
  };

  useEffect(() => {
    window.addEventListener('resize', resizeHandler);
    const renderData = {
      MarketProfilMobile: { Revenue: digitalVal, Downloads: physicalVal },
      MarketProfilPC: {
        Digital: digitalVal,
        Physical: physicalVal,
        TotalRevenue: totalRevenue,
        TotalPlayer: totalPlayer,
      },
      MarketProfilConsole: {
        Digital: digitalVal,
        Physical: physicalVal,
        TotalRevenue: totalRevenue,
        TotalPlayer: totalPlayer,
      },
    } as any;
    if (echarts.getMap('world')) {
      worldMapInstance(
        pageId == 'MarketProfilAnnualReport' ? digitalVal : renderData[pageId][mainSegment],
        `mpGameMap${pageId}`,
        mapColor,
      );
    }
    return () => {
      window.removeEventListener('resize', resizeHandler);
    };
  }, [map, digitalVal, physicalVal, totalRevenue, totalPlayer, regionsCountries]);

  // 窗口变化调整布局 提取
  const resizeHandler = () => {
    const mapDom = document.getElementById(`mpGameMap${pageId}`);
    if (mapDom) {
      const mapChart = echarts.init(mapDom);
      try {
        mapChart.resize();
      } catch (e) { }
    }

    const lineDom = document.getElementById('mpAnnualSummaryCharts');
    if (lineDom) {
      const lineChart = echarts.init(lineDom);
      try {
        lineChart.resize();
      } catch (e) { }
    }
  };

  // 折线图,
  // TODO: 整个组件是为了节约时间，尽快上线，从他处拷来，防止改动影响到其他页面，后面有时间需要进行调整修改
  const drawLine = (titleNow = '') => {
    const chartTempData = [] as any;
    const value = localStorage.getItem('marketProfileChartCard');
    const drawMap = {
      Revenue: 'revenue',
      Downloads: 'download',
      Digital: 'digitalRevenue',
      Physical: 'physicalRevenue',
      TotalRevenue: 'totalRevenue',
      TotalPlayer: 'totalPlayer',
    };

    const aAxis =
      lineTrendData[value == 'TotalRevenue' || value == 'TotalPlayer' ? 'totalAxis' : 'xAxis'];
    let itemKey = '';

    switch (value) {
      case 'Revenue':
        itemKey = 'revenue';
        break;
      case 'Downloads':
        itemKey = 'download';
        break;
      case 'Digital':
        itemKey = 'digitalRevenue';
        break;
      case 'Physical':
        itemKey = 'physicalRevenue';
        break;
      case 'TotalRevenue':
        itemKey = 'totalRevenue';
        break;
      case 'TotalPlayer':
        itemKey = 'totalPlayer';
        break;
    }

    selectedKeys?.map((item, index) => {
      const data = lineTrendData[item.gameId];

      // console.log('drawLine', { lineTrendData, titleNow, itemKey, data });

      if (data) {
        const obj = {
          name: toTranslateCountry(item.gameId) || item.gameId,
          type: 'line',
          // data: data[itemKey],
          data: data[drawMap[titleNow]],
          color: lineColor[index],
        };
        chartTempData.push(obj);
      }
    });
    drawLineChart(`mpGameMap${pageId}`, chartTempData, aAxis, titleNow);
  };

  const drawLineChart = (containId: string, data: any, xAxis: any, titleNow: string) => {
    const chartDom = document.getElementById(containId);

    // console.log('drawLineChart', { containId, data, xAxis, titleNow, chartDom });

    if (chartDom) {
      let myChart = echarts.getInstanceByDom(chartDom);
      if (myChart == null) {
        myChart = echarts.init(chartDom);
      }
      const lineFormatterTooltip = (params: any) => {
        return ChartBubble({
          data: params?.sort(sortArrDesc('value')),
          headerLeftTitle: '',
          titleNow,
          topChartsType: 'marketProfile-line',
          imageType: 'mobile',
          chartType: 'line',
          dateType: 'month',
          ifNoBarImg: true,
        });
      };

      const option = {
        title: {
          show: false,
        },
        tooltip: {
          trigger: 'axis',
          padding: 0,
          borderWidth: 0,
          formatter: lineFormatterTooltip,
        },
        legend: {
          show: true,
          bottom: 0,
          lineStyle: {
            opacity: 0,
          },
          icon: 'circle',
          type: 'scroll',
          itemWidth: 8,
        },
        grid: {
          top: '20px',
          left: '0%',
          right: '2%',
          bottom: '10%',
          containLabel: true,
        },
        xAxis: [
          {
            type: 'category',
            boundaryGap: true,
            data: xAxis,
            axisTick: { alignWithLabel: true },
            axisLine: {
              color: 'rgba(0,0,0,0.45)',
            },
          },
        ],
        yAxis: [
          {
            type: 'value',
            axisLine: {
              show: false,
            },
            axisLabel: {
              color: 'rgba(0,0,0,0.45)',
              formatter(value, index) {
                return formatNum(value);
              },
            },
            axisTick: {
              show: false,
            },
            splitLine: {
              lineStyle: {
                color: '#E9E9E9',
              },
            },
          },
        ],
        series: data,
      };
      option && myChart.setOption(option, true);
      myChart.resize();
    }
  };

  // chartType改变
  useEffect(() => {
    const value = localStorage.getItem('marketProfileChartCard');

    // console.debug('chartType change', { value });

    if (chartType == 'chart-map' && !mapLoading && map) {
      // console.debug('Rendering map with data', {
      //   map,
      //   digitalVal,
      //   physicalVal,
      //   totalRevenue,
      //   totalPlayer,
      // });

      let mapData = [] as any;

      if (value === 'Revenue' || value === 'Digital') {
        mapData = digitalVal;
      } else if (value === 'Downloads' || value === 'Physical') {
        mapData = physicalVal;
      } else if (value === 'TotalRevenue') {
        mapData = totalRevenue;
      } else if (value === 'TotalPlayer') {
        mapData = totalPlayer;
      }

      // if (echarts.getMap('world')) {
      if (mapData && mapData.length > 0) {
        worldMapInstance(mapData, `mpGameMap${pageId}`, mapColor);
      }
    } else if (chartType === 'discount-chart' && selectedKeys.length > 0) {
      drawLine(mainSegment);
    }
    // }, [chartType, locale, lineTrendData]);
  }, [
    map,
    mapLoading,
    locale,
    chartType,
    digitalVal,
    physicalVal,
    totalRevenue,
    totalPlayer,
    regionsCountries,
    lineTrendData,
  ]);

  // useEffect(() => {
  //   if (chartType === 'chart-map' && !mapLoading && map) {
  //     console.debug('Rendering map with data', {
  //       map,
  //       digitalVal,
  //       physicalVal,
  //       totalRevenue,
  //       totalPlayer,
  //     });

  //     const value = localStorage.getItem('marketProfileChartCard');
  //     let mapData = [] as any;

  //     if (value === 'Revenue' || value === 'Digital') {
  //       mapData = digitalVal;
  //     } else if (value === 'Downloads' || value === 'Physical') {
  //       mapData = physicalVal;
  //     } else if (value === 'TotalRevenue') {
  //       mapData = totalRevenue;
  //     } else if (value === 'TotalPlayer') {
  //       mapData = totalPlayer;
  //     }

  //     if (mapData && mapData.length > 0) {
  //       worldMapInstance(mapData, `mpGameMap${pageId}`, mapColor);
  //     }
  //   }
  // }, [
  //   map,
  //   mapLoading,
  //   chartType,
  //   digitalVal,
  //   physicalVal,
  //   totalRevenue,
  //   totalPlayer,
  //   regionsCountries,
  // ]);

  useEffect(() => {
    if (chartType == 'discount-chart' && selectedKeys.length > 0) {
      drawLine(mainSegment);
    }
  }, [selectedKeys, selectedKeysColor, regionsCountries, lineTrendData, locale]);

  // console.debug('getLocalStorage', { chart: localStorage.getItem('marketProfileChartCard') });

  // 地图数据重新渲染`
  const changeSegment = (value: any) => {
    onSegmentChange && onSegmentChange(value);
    localStorage.setItem('marketProfileChartCard', value);

    // console.debug('changeSegment', {
    //   value,
    //   chartType,
    //   digitalVal,
    //   physicalVal,
    //   totalRevenue,
    //   totalPlayer,
    // });

    if (chartType == 'discount-chart') {
      drawLine(value);
    } else {
      if (value == 'Revenue' || value == 'Digital') {
        refreshMapData(`mpGameMap${pageId}`, digitalVal, true);
      } else if (value == 'Downloads' || value == 'Physical') {
        refreshMapData(`mpGameMap${pageId}`, physicalVal);
      } else if (value == 'TotalRevenue') {
        refreshMapData(`mpGameMap${pageId}`, totalRevenue);
      } else if (value == 'TotalPlayer') {
        refreshMapData(`mpGameMap${pageId}`, totalPlayer);
      }
    }
  };

  return (
    <div className="chart-con">
      <div className="tablecard-filter">
        {options ? (
          <Segment
            value={mainSegment}
            onChange={(value: string) => changeSegment(value)}
            options={options}
          />
        ) : (
          ''
        )}
        {sortbyText ? (
          <div className="tablecard-txt">
            <span className="tablecard-txt-text">{sortbyText}</span>
            <div className="tablecard-txt-icon">
              ( <img src={ArrowBottom} alt="" /> )
            </div>
          </div>
        ) : (
          ''
        )}
      </div>
      <div className="echarts-line worldMap">
        <div className="mpGameMapMain" id={`mpGameMap${pageId}`}></div>
        {activeTab === 'chart-map' && (
          <div className="control">
            <span
              className="control-icon"
              onClick={() => {
                mapZoom(`mpGameMap${pageId}`, 'zoomIn');
              }}
            >
              <Icon type="plus" />
            </span>
            <span
              className="control-icon"
              onClick={() => {
                mapZoom(`mpGameMap${pageId}`, 'zoomOut');
              }}
            >
              <Icon type="minus" />
            </span>
          </div>
        )}
      </div>
    </div>
  );
}
